"use client";

import { useEffect, useState } from "react";

interface Avatar {
  id: string;
  slug: string;
  avatar_url: string | null;
  first_name: string | null;
  display_name: string;
}

interface SocialProofAvatarsProps {
  /** Quantos circulos sao exibidos visualmente (default 4) */
  count?: number;
  /** Intervalo em ms entre rotacoes de avatar (default 4000). 0 desliga rotacao. */
  rotateMs?: number;
  /** Tamanho dos circulos em Tailwind (default 'w-9 h-9') */
  size?: string;
  /** Cor da borda externa (default 'border-white') */
  borderClass?: string;
}

// Cache compartilhado entre instancias - evita N fetches na mesma pagina
let sharedPool: Avatar[] | null = null;
let sharedPromise: Promise<Avatar[]> | null = null;

async function fetchPool(): Promise<Avatar[]> {
  if (sharedPool) return sharedPool;
  if (sharedPromise) return sharedPromise;
  sharedPromise = fetch("/api/public/social-proof-avatars?count=30")
    .then((r) => r.json())
    .then((d) => {
      const arr = (d.avatars || []) as Avatar[];
      sharedPool = arr;
      return arr;
    })
    .catch(() => {
      sharedPool = [];
      return [];
    });
  return sharedPromise;
}

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

// Paleta deterministica baseada no nome - cores violet/emerald/amber consistentes
function colorForName(name: string): string {
  const palette = [
    "from-violet-400 to-violet-600",
    "from-emerald-400 to-emerald-600",
    "from-amber-400 to-amber-600",
    "from-sky-400 to-sky-600",
    "from-rose-400 to-rose-600",
    "from-indigo-400 to-indigo-600",
    "from-teal-400 to-teal-600",
    "from-fuchsia-400 to-fuchsia-600",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
}

/**
 * Stack de avatares de clientes eBuz com rotacao automatica.
 * Cada avatar e clicavel (abre /expert/{slug} em nova aba).
 * Hover desktop mostra tooltip com nome.
 * Mobile: avatar recem-rotacionado pulsa pra chamar atencao.
 * Sem foto = inicial colorida (gradient deterministico por nome).
 */
export default function SocialProofAvatars({
  count = 4,
  rotateMs = 4000,
  size = "w-9 h-9",
  borderClass = "border-white",
}: SocialProofAvatarsProps) {
  const [pool, setPool] = useState<Avatar[]>([]);
  const [visibleIndexes, setVisibleIndexes] = useState<number[]>([]);
  const [pulseSlot, setPulseSlot] = useState<number | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchPool().then((arr) => {
      if (!mounted) return;
      setPool(arr);
      const initial = Array.from({ length: Math.min(count, arr.length) }, (_, i) => i);
      setVisibleIndexes(initial);
    });
    return () => {
      mounted = false;
    };
  }, [count]);

  useEffect(() => {
    if (!rotateMs || pool.length <= count) return;
    const timer = setInterval(() => {
      setVisibleIndexes((current) => {
        const slot = Math.floor(Math.random() * current.length);
        const available = pool.map((_, i) => i).filter((i) => !current.includes(i));
        if (available.length === 0) return current;
        const newIdx = available[Math.floor(Math.random() * available.length)];
        const next = [...current];
        next[slot] = newIdx;
        // Marca esse slot pra pulsar (chama atencao no mobile)
        setPulseSlot(slot);
        setTimeout(() => setPulseSlot((p) => (p === slot ? null : p)), 1800);
        return next;
      });
    }, rotateMs);
    return () => clearInterval(timer);
  }, [pool, count, rotateMs]);

  const slots: Array<Avatar | null> = [];
  for (let i = 0; i < count; i++) {
    const idx = visibleIndexes[i];
    slots.push(idx !== undefined && pool[idx] ? pool[idx] : null);
  }

  return (
    <div className="flex -space-x-2">
      {slots.map((avatar, i) => {
        const isPulsing = pulseSlot === i;
        const initials = avatar ? getInitials(avatar.display_name) : "";
        const colorClass = avatar ? colorForName(avatar.display_name) : "from-ink-7 to-ink-6";

        const circle = (
          <div
            className={`${size} rounded-full border-2 ${borderClass} overflow-hidden bg-gradient-to-br ${colorClass} flex items-center justify-center text-white text-xs font-bold shadow-sm transition-transform duration-300 group-hover:scale-110 group-hover:z-50 ${
              isPulsing ? "animate-pulse-ring" : ""
            }`}
          >
            {avatar?.avatar_url ? (
              /* eslint-disable-next-line @next/next/no-img-element */
              <img
                src={avatar.avatar_url}
                alt={avatar.display_name}
                className="w-full h-full object-cover"
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  // Se a imagem falhar, esconde e cai pro initial
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <span aria-hidden>{initials}</span>
            )}
          </div>
        );

        if (!avatar) {
          // Slot vazio - placeholder cinza nao clicavel
          return (
            <div key={i} className="relative" style={{ zIndex: count - i }}>
              {circle}
            </div>
          );
        }

        return (
          <a
            key={i}
            href={`/expert/${avatar.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Ver perfil de ${avatar.display_name}`}
            className="relative group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-full"
            style={{ zIndex: count - i }}
          >
            {circle}
            {/* Tooltip desktop (hidden no mobile) */}
            <span
              className="hidden sm:block absolute left-1/2 -translate-x-1/2 -top-9 px-2.5 py-1 rounded-md bg-ink-0 text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50"
              role="tooltip"
            >
              Ver {avatar.first_name || avatar.display_name} →
            </span>
          </a>
        );
      })}
    </div>
  );
}
